import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { 
  AlertCircle, 
  DollarSign, 
  Clock, 
  CheckCircle2, 
  TrendingUp, 
  TrendingDown,
  ArrowRight,
  Building2,
  FileText,
  Calendar
} from "lucide-react"
import { useNavigate } from "react-router-dom"

// Sample data
const recentDisputes = [
  {
    id: "DSP-2024-1847",
    priority: "Critical",
    amount: 47500,
    company: "Acme Manufacturing Corp",
    reason: "Quality Issues",
    daysOpen: 12,
  },
  {
    id: "DSP-2024-1846",
    priority: "High",
    amount: 32800,
    company: "TechStart Inc",
    reason: "Billing Discrepancy",
    daysOpen: 8,
  },
  {
    id: "DSP-2024-1845",
    priority: "Medium",
    amount: 15200,
    company: "Global Solutions Ltd",
    reason: "Service Not Delivered",
    daysOpen: 5,
  },
  {
    id: "DSP-2024-1844",
    priority: "High",
    amount: 28900,
    company: "Enterprise Systems Co",
    reason: "Pricing Dispute",
    daysOpen: 15,
  },
  {
    id: "DSP-2024-1843",
    priority: "Low",
    amount: 8500,
    company: "Small Business LLC",
    reason: "Billing Discrepancy",
    daysOpen: 3,
  },
]

const disputesByPriority = [
  { priority: "Critical", count: 8, amount: 387200, variant: "destructive", accent: undefined },
  { priority: "High", count: 15, amount: 542800, variant: "warning", accent: "orange" },
  { priority: "Medium", count: 23, amount: 398500, variant: "default", accent: undefined },
  { priority: "Low", count: 12, amount: 89300, variant: "secondary", accent: undefined },
]

const disputeReasons = [
  { reason: "Quality Issues", count: 24, percentage: 41 },
  { reason: "Billing Discrepancy", count: 18, percentage: 31 },
  { reason: "Service Not Delivered", count: 10, percentage: 17 },
  { reason: "Pricing Dispute", count: 6, percentage: 11 },
]

const getPriorityBadgeVariant = (priority: string): { variant?: any; accent?: any } => {
  switch (priority) {
    case "Critical": return { variant: "destructive" }
    case "High": return { variant: "warning", accent: "orange" }
    case "Medium": return { variant: "default" }
    case "Low": return { variant: "secondary" }
    default: return { variant: "default" }
  }
}

const getPriorityBorderColor = (priority: string) => {
  switch (priority) {
    case "Critical": return "rgb(239, 68, 68)"
    case "High": return "rgb(249, 115, 22)"
    case "Medium": return "rgb(148, 163, 184)"
    case "Low": return "rgb(156, 163, 175)"
    default: return "rgb(156, 163, 175)"
  }
}

export default function DisputesDashboardPage() {
  const navigate = useNavigate()

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header Section */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dispute Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Monitor dispute metrics and track resolution progress
          </p>
        </div>
        <Button 
          onClick={() => navigate("/disputes/all")}
          className="bg-teal-600 hover:bg-teal-700"
        >
          View All Disputes
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {/* Metrics Cards Row */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Active Disputes */}
        <Card className="border-l-4 border-l-accent-orange-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Disputes</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">58</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendingDown className="mr-1 h-3 w-3 text-green-600" />
              <span className="text-green-600 font-medium">-12%</span>
              <span className="ml-1">vs last week</span>
            </div>
          </CardContent>
        </Card>

        {/* Amount in Dispute */}
        <Card className="border-l-4 border-l-accent-destructive-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Amount in Dispute</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$1.42M</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendingUp className="mr-1 h-3 w-3 text-red-600" />
              <span className="text-red-600 font-medium">+8%</span>
              <span className="ml-1">vs last week</span>
            </div>
          </CardContent>
        </Card>

        {/* Avg Resolution Time */}
        <Card className="border-l-4 border-l-accent-info-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Resolution Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">14.3 days</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendingDown className="mr-1 h-3 w-3 text-green-600" />
              <span className="text-green-600 font-medium">-18%</span>
              <span className="ml-1">vs last week</span>
            </div>
          </CardContent>
        </Card>

        {/* Resolution Rate */}
        <Card className="border-l-4 border-l-accent-success-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Resolution Rate</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">87.5%</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendingUp className="mr-1 h-3 w-3 text-green-600" />
              <span className="text-green-600 font-medium">+5.2%</span>
              <span className="ml-1">vs last week</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two-Column Mid Section */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Disputes by Priority */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Disputes by Priority</CardTitle>
            <CardDescription>Current distribution across priority levels</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {disputesByPriority.map((item) => (
                <div key={item.priority} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge variant={item.variant as any} accent={item.accent as any}>
                      {item.priority}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {item.count} disputes
                    </span>
                  </div>
                  <span className="text-sm font-medium">
                    ${(item.amount / 1000).toFixed(0)}K
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Common Dispute Reasons */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Common Dispute Reasons</CardTitle>
            <CardDescription>Top reasons for customer disputes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {disputeReasons.map((item) => (
                <div key={item.reason} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{item.reason}</span>
                    <span className="text-muted-foreground">{item.count}</span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-600 rounded-full"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Dispute Activity Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Recent Dispute Activity</CardTitle>
          <CardDescription>Latest disputes requiring attention</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-4">
              {recentDisputes.map((dispute) => (
                <Card
                  key={dispute.id}
                  className="border-l-4"
                  style={{
                    borderLeftColor: getPriorityBorderColor(dispute.priority)
                  }}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{dispute.id}</span>
                        <Badge {...getPriorityBadgeVariant(dispute.priority)}>
                          {dispute.priority}
                        </Badge>
                      </div>
                      <span className="text-lg font-bold">
                        ${dispute.amount.toLocaleString()}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building2 className="h-4 w-4" />
                        <span>{dispute.company}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <FileText className="h-4 w-4" />
                          <span>{dispute.reason}</span>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span>{dispute.daysOpen} days open</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-4"
                      onClick={() => navigate("/disputes/all")}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  )
}
